import { useMutation, useQuery } from '@tanstack/react-query';

import type {
  ApiError,
  ApiMarkDeceasedValues,
  ApiUser,
  ApiUserMailConfiguration,
  UserApi,
} from '@izimi/api';
import type { FirstParameter } from '@izimi/utils';

import {
  userQueryKeys,
  useSetUserCache,
  useSetUserMailConfiguration,
  useSetUserPreferencesCache,
} from './cache';

const STALE_30_MINUTES = 1800000;

const createUserModule = ({ userApi }: { userApi: UserApi }) => {
  const useCurrentUser = (enabled = true) => {
    const { data, isLoading, error } = useQuery({
      queryKey: userQueryKeys.getCurrentUser(),
      queryFn: () => userApi.getCurrentUser(),
      enabled,
      staleTime: STALE_30_MINUTES,
    });

    return {
      currentUser: data,
      error,
      isLoading,
    };
  };

  const useRegisterUser = () => {
    const { mutateAsync, isPending, error } = useMutation<
      ApiUser,
      ApiError,
      FirstParameter<UserApi['registerUser']>
    >({ mutationFn: (values) => userApi.registerUser(values) });

    return {
      registerUser: mutateAsync,
      isPending,
      error,
    };
  };

  const useUpdateUser = () => {
    const setUserCache = useSetUserCache();
    const { mutateAsync, isPending, error } = useMutation<
      ApiUser,
      ApiError,
      FirstParameter<UserApi['updateUser']>
    >({
      mutationFn: (values) => userApi.updateUser(values),
      onSuccess(user) {
        setUserCache(user);
      },
    });

    return {
      updateUser: mutateAsync,
      isPending,
      error,
    };
  };

  const useMarkUserDeceased = () => {
    const { mutateAsync, isPending, error } = useMutation<void, ApiError, ApiMarkDeceasedValues>({
      mutationFn: (values) => userApi.markDeceased(values),
    });

    return {
      markUserDeceased: mutateAsync,
      isPending,
      error,
    };
  };

  const useUnmarkUserDeceased = () => {
    const { mutateAsync, isPending, error } = useMutation<void, ApiError, string>({
      mutationFn: (nrn) => userApi.unmarkDeceased(nrn),
    });

    return {
      unmarkUserDeceased: mutateAsync,
      isPending,
      error,
    };
  };

  const useCancelAccountDeactivation = () => {
    const { mutateAsync, isPending, error } = useMutation<undefined, ApiError, void>({
      mutationFn: () => userApi.cancelDeactivation(),
    });

    return {
      cancelAccountDeactivation: mutateAsync,
      isPending,
      error,
    };
  };

  const useStartAccountDeactivation = () => {
    const { mutateAsync, isPending, error } = useMutation<undefined, ApiError, void>({
      mutationFn: () => userApi.startDeactivation(),
    });

    return {
      startAccountDeactivation: mutateAsync,
      isPending,
      error,
    };
  };

  const useCompleteAccountDeactivation = () => {
    const { mutateAsync, isPending, error } = useMutation<undefined, ApiError, string>({
      mutationFn: (code) => userApi.completeDeactivation({ deactivationCode: code }),
    });

    return {
      completeAccountDeactivation: mutateAsync,
      isPending,
      error,
    };
  };

  const useFrontendSyncConfiguration = ({ enabled }: { enabled: boolean }) => {
    const { data, isLoading, error } = useQuery({
      queryKey: userQueryKeys.getFrontendSyncConfiguration(),
      queryFn: () => userApi.getFrontendSyncConfiguration(),
      enabled,
    });

    return {
      frontendSyncConfiguration: data,
      error,
      isLoading,
    };
  };

  const useUserPreferences = () => {
    const { data, isLoading, error } = useQuery({
      queryKey: userQueryKeys.getUserPreferences(),
      queryFn: () => userApi.getUserPreferences(),
    });

    return {
      userPreferences: data,
      error,
      isLoading,
    };
  };

  const useUpdateUserPreferences = () => {
    const setUserPreferencesCache = useSetUserPreferencesCache();
    const { mutateAsync, isPending, error } = useMutation<
      Awaited<ReturnType<UserApi['getUserPreferences']>>,
      ApiError,
      FirstParameter<UserApi['setUserPreferences']>
    >({
      mutationFn: (values) => userApi.setUserPreferences(values),
      onSuccess(user) {
        setUserPreferencesCache(user);
      },
    });

    return {
      updateUserPreferences: mutateAsync,
      isPending,
      error,
    };
  };

  const useMailConfiguration = () => {
    const { data, isLoading, error } = useQuery<ApiUserMailConfiguration>({
      queryKey: userQueryKeys.getMailConfiguration(),
      queryFn: () => userApi.getUserMailConfiguration(),
    });

    return {
      data: data ?? {},
      totalActive: data?.subscriptions?.filter((sub) => sub.active).length ?? 0,
      error,
      isLoading,
    };
  };

  const useUpdateMailConfiguration = () => {
    const setUserMailConfiguration = useSetUserMailConfiguration();
    const { mutateAsync, isPending, error } = useMutation<
      ApiUserMailConfiguration,
      ApiError,
      ApiUserMailConfiguration
    >({
      mutationFn: (values) => userApi.setUserMailConfiguration(values),
      onSuccess: (data) => setUserMailConfiguration(data),
    });

    return {
      updateMailConfiguration: mutateAsync,
      isPending,
      error,
    };
  };

  return {
    useCurrentUser,
    useFrontendSyncConfiguration,
    useRegisterUser,
    useUpdateUser,
    useMarkUserDeceased,
    useUnmarkUserDeceased,
    useCancelAccountDeactivation,
    useStartAccountDeactivation,
    useCompleteAccountDeactivation,
    useUserPreferences,
    useUpdateUserPreferences,
    useMailConfiguration,
    useUpdateMailConfiguration,
  };
};

export default createUserModule;
